import { useState, useEffect } from "react";
import PdfIcon from "../../Images/pdf_icon.svg";
import { API_URL } from "../../config/api";
import { getFileNameFromUrl } from "../../config/Filenameinfo";
import ToastMessage from "../../config/ToastMessage";

export default function StudentAssignmentGrade({
  isOpen,
  onClose,
  submission,
  student,
  assignment,
  demo,
  lab,
  quiz,
  type = "assignment",
}) {
  const getMaxPoints = () => {
    if (type === "demo" && demo) return demo.max_points;
    if (type === "lab" && lab) return lab.max_points;
    if (type === "quiz" && quiz) return quiz.max_points;
    if (type === "assignment" && assignment) return assignment.max_points;
    return 100;
  };

  const getMinPoints = () => {
    if (type === "demo" && demo) return demo.min_points ?? 0;
    if (type === "lab" && lab) return lab.min_points ?? 0;
    if (type === "quiz" && quiz) return quiz.min_points ?? 0;
    if (type === "assignment" && assignment) return assignment.min_points ?? 0;
    return 0;
  };

  const [isResubmission, setIsResubmission] = useState(false);
  const [isAddScore, setIsAddScore] = useState(false);
  const [score, setScore] = useState("");
  const [feedback, setFeedback] = useState("");
  const [visible, setVisible] = useState(false);
  const [toast, setToast] = useState({ show: false, type: "error", message: "" });

  useEffect(() => {
    if (isOpen) setVisible(true);
    else setTimeout(() => setVisible(false), 300);
  }, [isOpen]);

  useEffect(() => {
    if (submission) {
      setScore(submission.grade ?? "");
      setFeedback(submission.feedback ?? "");
      setIsAddScore(submission.grade !== null && submission.grade !== undefined);
    }
  }, [submission]);

  if (!visible || !submission || !student) return null;

  const getApiUrl = () => {
    if (type === "lab") return `${API_URL}/labs/lab-submission/grade`;
    if (type === "demo") return `${API_URL}/demo/demo_submission/grade`;
    return `${API_URL}/assignments/assignment-submission/grade`;
  };

  const handleSave = async () => {
    if (isAddScore && score === "") {
      setToast({ show: true, type: "error", message: "Please enter score" });
      return;
    }

    let finalScore = null;
    if (isAddScore) {
      const numScore = Number(score);
      const max = getMaxPoints();
      if (numScore > max) {
        setToast({
          show: true,
          type: "error",
          message: `Please enter a mark within the maximum mark (${max})`,
        });
        return;
      }
      finalScore = numScore;
    }

    let feedback_status = submission.feedback_status || "Submitted";
    if (isResubmission) feedback_status = "Re-submission";
    else if (finalScore !== null || feedback.trim()) feedback_status = "Submitted";

    const payload = {
      submission_id: submission.submission_id,
      grade: finalScore,
      feedback: feedback.trim() || null,
      feedback_status,
    };

    console.log("GRADE PAYLOAD 👉", payload);
    console.log("API URL 👉", getApiUrl());

    try {
      const token = localStorage.getItem("token") || localStorage.getItem("access_token");

      const response = await fetch(getApiUrl(), {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (!response.ok) {
        setToast({
          show: true,
          type: "error",
          message: "Error: " + (result?.detail || "Failed to save grade"),
        });
        return;
      }

      console.log("API Response 👉", result);
      setToast({ show: true, type: "success", message: "Grade saved successfully!" });
      onClose();
    } catch (error) {
      console.error(error);
      setToast({ show: true, type: "error", message: "Something went wrong!" });
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex justify-end bg-black/40">
        <div
          className={`w-96 h-full bg-white shadow-xl relative overflow-y-auto transform transition-transform duration-300 ease-in-out ${
            isOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          {/* Header */}
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <h1 className="text-zinc-800 text-xl font-bold">Student Grade Panel</h1>
            <button onClick={onClose} className="w-3 h-3 text-gray-700 hover:text-gray-900">
              <svg viewBox="0 0 12 12" fill="currentColor">
                <path d="M11.25 1.8075L10.1925 0.75L6 4.9425L1.8075 0.75L0.75 1.8075L4.9425 6L0.75 10.1925L1.8075 11.25L6 7.0575L10.1925 11.25L11.25 10.1925L7.0575 6L11.25 1.8075Z" />
              </svg>
            </button>
          </div>

          <div className="p-6">

            {/* Student Info */}
            <div className="flex items-center gap-4 mb-8">
              {student.avatar_url ? (
                <img
                  src={student.avatar_url}
                  alt={student.full_name}
                  className="w-24 h-24 rounded-full shadow-md border-2 border-gray-200 object-cover"
                />
              ) : (
                <div className="w-24 h-24 rounded-full shadow-md border-2 border-gray-200 bg-cyan-100 flex items-center justify-center text-2xl font-semibold text-cyan-600">
                  {student.full_name?.charAt(0)?.toUpperCase() || "S"}
                </div>
              )}
              <div>
                <div className="text-zinc-800 text-base font-semibold mb-1">
                  {student.full_name}
                </div>
                <div className="flex items-start text-gray-700 text-xs">
                  Student ID : {student.user_id}
                </div>
              </div>
            </div>

            {/* Submitted On */}
            <div className="mb-6">
              <div className="relative bg-zinc-100 rounded-md border border-neutral-200 p-3">
                <div className="absolute -top-2 left-4 bg-zinc-100 px-2 text-xs text-neutral-600">
                  Submitted On
                </div>
                <div className="text-zinc-800 text-base">
                  {submission?.submitted_at
                    ? new Date(submission.submitted_at).toLocaleDateString("en-US", {
                        month: "short", day: "numeric", year: "numeric",
                      })
                    : "--"}
                </div>
              </div>
            </div>

            {/* File */}
            <div className="mb-6">
              <div className="bg-white rounded border border-gray-200 p-2.5 flex items-center gap-3 mb-2">
                <div className="w-11 h-11 relative flex-shrink-0">
                  <img src={PdfIcon} alt="icon" />
                </div>
                <div className="flex-1 text-zinc-600 text-xs leading-4 break-all">
                  {getFileNameFromUrl(submission?.file_url)}
                </div>
              </div>
              <button
                onClick={async () => {
                  try {
                    if (!submission?.file_url) return;
                    const blobPath = submission.file_url.split("/skillmd-dev-container/")[1];
                    const response = await fetch(`${API_URL}/azure-upload/download/${blobPath}`);
                    if (!response.ok) throw new Error("Failed to generate download link");
                    const data = await response.json();
                    const link = document.createElement("a");
                    link.href = data.download_url;
                    link.target = "_blank";
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  } catch (error) {
                    console.error("Download error:", error);
                  }
                }}
                className="text-cyan-500 text-xs underline hover:text-cyan-600"
              >
                Download File
              </button>
            </div>

            {/* ✅ Checkboxes — mutually exclusive */}
            <div className="flex items-center gap-7 mb-6">

              <label className="flex items-center gap-3 cursor-pointer">
                <span>Re-submission</span>
                <div
                  onClick={() => {
                    setIsResubmission(!isResubmission);
                    if (!isResubmission) {
                      setIsAddScore(false);  // ✅ deselect Add Score
                      setScore("");
                    }
                  }}
                  className={`w-6 h-6 rounded border flex items-center justify-center cursor-pointer ${
                    isResubmission ? "bg-teal-400 border-teal-400" : "bg-white border-stone-300"
                  }`}
                >
                  {isResubmission && (
                    <svg className="w-4 h-4 text-white" viewBox="0 0 12 9">
                      <path d="M1 4.5L4.5 8L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
              </label>

              <label className="flex items-center gap-3 cursor-pointer">
                <span>Add Score</span>
                <div
                  onClick={() => {
                    setIsAddScore(!isAddScore);
                    if (!isAddScore) {
                      setIsResubmission(false); // ✅ deselect Re-submission
                    } else {
                      setScore("");
                    }
                  }}
                  className={`w-6 h-6 rounded border flex items-center justify-center cursor-pointer ${
                    isAddScore ? "bg-teal-400 border-teal-400" : "bg-white border-stone-300"
                  }`}
                >
                  {isAddScore && (
                    <svg className="w-4 h-4 text-white" viewBox="0 0 12 9">
                      <path d="M1 4.5L4.5 8L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
              </label>

            </div>

            {/* ✅ Score — only when Add Score is checked */}
            {isAddScore && (
              <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-medium text-gray-700">Score</label>
                  <span className="text-sm text-gray-500">Max Points: {getMaxPoints()}</span>
                </div>
                <input
                  type="number"
                  value={score}
                  onChange={(e) => setScore(e.target.value)}
                  placeholder={`Enter score (max: ${getMaxPoints()})`}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
            )}

            {/* ✅ Feedback — only when either checkbox is selected */}
            {(isAddScore || isResubmission) && (
              <div className="mb-6">
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Feedback
                </label>
                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Enter your feedback here..."
                  rows={3}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
            )}

            {/* Buttons */}
            <div className="flex gap-5">
              <button
                onClick={onClose}
                className="flex-1 h-11 rounded border border-teal-400 text-teal-400 hover:bg-teal-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="flex-1 h-11 rounded bg-teal-400 text-white hover:bg-teal-500"
              >
                Save
              </button>
            </div>

          </div>
        </div>
      </div>

      {toast.show && (
        <ToastMessage
          type={toast.type}
          message={toast.message}
          onClose={() => setToast({ show: false, type: "error", message: "" })}
        />
      )}
    </>
  );
}